<template lang="pug">
div
  //- Fetching
  template(v-if="isFetching")
    v-skeleton-loader.px-4(type="list-item-three-line@5")
  template(v-else)
    //- Navigation tabs
    div.d-flex.align-center.text-sm
      v-btn.ml-n3.mr-2(text x-small icon @click="toPatientDatabase")
        v-icon(color="neutral700" :size="20") mdi-arrow-left
      v-hover(v-slot="{ hover }")
        span.font-medium.clickable.px-2.round-lg(:class="[{ 'neutral300':hover }]" @click="toPatientDatabase") {{ $t('patient_database') }}
      v-icon.mx-2(small) mdi-chevron-right
      span.font-regular {{ $t('patient_profile') }}
    //- Profile items
    .d-flex.mt-4
      .mr-4
        //- Profile image
        v-img.round-xl(v-if="patient.profile_image" width="60" height="80" :src="patient.profile_image.file" cover)
          template(v-slot:placeholder)
            v-row(class="fill-height ma-0" align="center" justify="center")
              v-progress-circular(indeterminate color="grey")
        v-card.neutral300.fill-height.round-xl(v-else flat width="60" height="80")
          div.d-flex.align-center.justify-center.fill-height
            v-icon(size="30") $yzi-user-03
      //- Profile info
      div
        .text-lg.font-semibold.mb-1 {{ patient.name }}
        .text-sm {{ patient.ic }}
        .text-sm(v-if="patient.dob") {{ $options.filters.getAgeByYearMonthDay(patient.dob) | displayAgeByYearMonthDay }} old
    //- Analytics
    v-card.rounded-lg.mt-6.neutral200.analytic-border(outlined)
      v-row(no-gutters)
        v-col.text-center.pa-2
          .text-xs.neutral500--text {{ $t('total_spend') }}
          .text-sm.no-wrap {{ patient.lifetime_value | displayPrice }}
        v-divider(vertical)
        v-col.text-center.pa-2
          .text-xs.neutral500--text {{ $t('total_visit') }}
          .text-sm.no-wrap {{ patient.total_visit }}
        v-divider(vertical)
        v-col.text-center.pa-2
          .text-xs.neutral500--text {{ $t('since') }}
          .text-sm.no-wrap {{ patient.since }}
    //- Patient details
    div.mt-8
      .d-flex.justify-space-between.align-center.mb-4
        .text-md.font-semibold {{ $t('patient_details') }}
        v-btn.button-xs.text-xs.underline.font-semibold(text small @click="onEditPatient") {{ $t('edit') }}
      template(v-for="detail in detailItems")
        .mb-3
          .text-sm.neutral500--text.font-semibold {{ detail.text }}
          .text-sm(v-if="detail.value === 'staff_ids'" v-html="patient[detail.value]")
          .text-sm(v-else) {{ patient[detail.value] }}
      //- Underlying Conditions
      .mb-3
        .text-sm.neutral500--text.font-semibold {{ $t('underlying_conditions') }}
        yz-truncated-list(v-if="underlyingConditions.length" :items="underlyingConditions" text-item="name")
      //- Allergies
      .mb-3
        .text-sm.neutral500--text.font-semibold {{ $t('allergies') }}
        yz-truncated-list(v-if="allergies.length" :items="allergies" text-item="name")
  .dialogs
    // New add patient dialog
    add-patient(v-if="openAddPatientDialog" :open.sync="openAddPatientDialog" :selectedPatient="patient" @closed="onCloseEditPatient($event)")
    // Old add patient dialog (remove when not needed)
    old-add-patient(v-if="openOldAddPatientDialog" :open.sync="openOldAddPatientDialog" :selectedPatient="patient" @closed="onCloseEditPatient($event)")
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters, mapActions, mapState } from 'vuex'
import AddPatient from '@/components/clinic/patient/AddPatient.vue'
import OldAddPatient from '@/components/clinic/patient/OldAddPatient.vue'

export default {
  components: {
    AddPatient, OldAddPatient
  },
  data() {
    return {
      isFetching: true,
      patient: null,
      route: { name: 'ClinicPatientList' },
      detailItems: [
        {
          text:'Email',
          value:'email',
        },
        {
          text:'Phone',
          value:'phone',
        },
        {
          text:'Birthday',
          value:'formatted_birthday',
        },
        {
          text:'Gender',
          value:'capitalize_gender',
        },
        {
          text:'Nationality',
          value:'capitalize_nationality',
        },
        {
          text:'Race',
          value:'capitalize_race',
        },
        {
          text:'Religion',
          value:'capitalize_religion',
        },
        {
          text:'Address',
          value:'full_address',
        },
        {
          text:'Emergency Contact',
          value:'emergency',
        },
        {
          text:'Staff IDs',
          value:'staff_ids',
        },
      ],
      openAddPatientDialog: false,
      openOldAddPatientDialog: false,
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
    }),
    patientId () {
      return this.$route.params.id
    },
    underlyingConditions () {
      return this.patient?.record?.underlying_conditions
        ? this.patient.record.underlying_conditions
        : []
    },
    allergies () {
      return this.patient?.record?.allergies
        ? this.patient.record.allergies
        : []
    }
  },
  mounted () {
    this.fetchPatient()
    this.saveRoute()
    this.setPopState()
  },
  beforeDestroy () {
    window.removeEventListener('popstate', this.handlePopState)
  },
  methods: {
    ...mapActions(['showMessage']),
    saveRoute () {
      if (this.$route.params && this.$route.params.data && Object.keys(this.$route.params.data).length) {
        this.route.params = { ...this.$route.params.data }
      }
    },
    setPopState () {
      history.pushState(null, document.title, window.location.href)
      window.addEventListener('popstate', this.handlePopState)
    },
    displayDate (value) {
      if (!value) {
        return null
      }
      const date = DateTime.fromISO(value)
      return `${date.toFormat('dd MMM yyyy')}`
    },
    getAddressInString (addr) {
      if (!addr) {
        return null
      }
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    getEmergencyContact (contact) {
      let emergency_contact = ''
      if (contact.name) {
        emergency_contact += contact.name
      } if (contact.phone) {
        if (emergency_contact) {
          emergency_contact += ' - '
        }
        emergency_contact += contact.phone
      }
      return emergency_contact
    },
    getPanelStaffIds (panels) {
      if (!panels || !Array.isArray(panels) || panels.length === 0) {
        return ''
      }
      const panelStaffIds = panels
        .filter(item => item.panel && item.panel.name)
        .map(item => {
          const panelName = item.panel.name
          const staffId = item.staff_id || ''
          return staffId ? `${panelName}: ${staffId}` : panelName
        })

      if (panelStaffIds.length === 0) {
        return '-'
      }

      return panelStaffIds
        .map((entry, index) => `${index + 1}. ${entry}`)
        .join('<br>')
    },
    toPatientDatabase () {
      return this.$router.push(this.route)
    },
    async fetchPatient () {
      try {
        this.isFetching = true
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patientId}`)
        this.patient = this.sanitizeData(data)
        this.isFetching = false
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    sanitizeData (data) {
      // birthday
      const formatted_birthday = data.dob ? this.displayDate(data.dob) : null
      // address
      const full_address = data.address ? this.getAddressInString(data.address) : null
      // gender
      const capitalize_gender = data.gender ? this.$options.filters.capitalize(data.gender) : null
      // nationality
      const capitalize_nationality = data.nationality ? this.$options.filters.capitalize(data.nationality) : null
      // race
      const capitalize_race = data.race ? this.$options.filters.capitalize(data.race) : null
      // religion
      const capitalize_religion = data.religion ? this.$options.filters.capitalize(data.religion) : null
      // emergency contact
      const emergency = (data.emergency_contact.name || data.emergency_contact.phone) ? this.getEmergencyContact(data.emergency_contact) : null
      // since
      const since = data.record_date ? DateTime.fromISO(data.record_date).toFormat('d MMM yyyy') : null
      // staff_ids (panel names and staff IDs)
      const staff_ids = this.getPanelStaffIds(data.panels)

      return {
        ...data,
        formatted_birthday,
        full_address,
        emergency,
        capitalize_gender,
        capitalize_nationality,
        capitalize_race,
        capitalize_religion,
        since,
        staff_ids,
      }
    },
    onEditPatient () {
      this.openAddPatientDialogVersion()
      // this.openAddPatientDialog = true
    },
    onCloseEditPatient (event) {
      if (event === 'cancel') {
        return
      }
      this.fetchPatient()
    },
    handlePopState() {
      this.toPatientDatabase()
      history.pushState(null, document.title, window.location.href)
    },
    openAddPatientDialogVersion () {
      if (this.member.extra_data?.new_registration) {
        this.openAddPatientDialog = true
        return
      }
      this.openOldAddPatientDialog = true
    },
  },
}
</script>

<style scoped>
.main {
  position: absolute;
  top: 0;
  display: flex;
  flex-direction: column;
  width: 100% !important;
  height: 100% !important;
}
.analytic-border {
  border: 1px var(--v-neutral400) solid !important;
}
</style>